import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/insights/KpiCard";
import { InsightChart } from "@/components/insights/InsightChart";
import { SqlPanel } from "@/components/insights/SqlPanel";
import { Search, Sparkles } from "lucide-react";

export default function AskTheData() {
  const [department, setDepartment] = useState("Inspection Mgmt");
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState(null);

  const { data: departments } = useQuery({
    queryKey: ["insight-departments"],
    queryFn: async () => (await api.get("/insights/departments")).data,
  });

  const queryMutation = useMutation({
    mutationFn: async (q) => (await api.post("/insights/query", { department, question: q })).data,
    onSuccess: setResult,
  });

  const currentDept = departments?.find((d) => d.department === department);

  const ask = (q) => {
    setQuestion(q);
    queryMutation.mutate(q);
  };

  return (
    <main className="max-w-6xl mx-auto py-8 px-6" data-testid="ask-the-data-page">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-display font-medium text-[#141E1A]">Ask the Data</h1>
        <p className="text-sm text-[#5A6B63] mt-1">
          Pick a department, ask a question in plain English. This screen simulates the future NL-to-query engine.
        </p>
      </div>

      <div className="bg-white border border-[#DCE3DF] rounded-[10px] shadow-[0_4px_20px_rgba(20,30,26,0.06)] p-6 mb-6 space-y-4">
        <div>
          <label className="text-xs font-mono uppercase tracking-[0.08em] text-[#5A6B63] font-semibold">Department</label>
          <Select
            value={department}
            onValueChange={(v) => {
              setDepartment(v);
              setResult(null);
            }}
          >
            <SelectTrigger data-testid="insights-department-select" className="mt-1.5 h-10 w-64 border-[#DCE3DF]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(departments || []).map((d) => (
                <SelectItem key={d.department} value={d.department} data-testid={`insights-department-option-${d.department.toLowerCase().replace(/\s+/g, "-")}`}>
                  {d.department}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-3 items-start">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#5A6B63] absolute left-3 top-3" />
            <Textarea
              data-testid="insights-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask a question about this department…"
              className="pl-9 min-h-[44px] border-[#DCE3DF]"
            />
          </div>
          <Button
            data-testid="insights-ask-button"
            onClick={() => ask(question)}
            disabled={!question || queryMutation.isPending}
            className="bg-[#0E6E52] hover:bg-[#1D3730] text-white h-11 px-6 rounded-md transition-colors duration-200"
          >
            {queryMutation.isPending ? "Thinking…" : "Ask"}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {currentDept?.suggested_questions?.map((q, i) => (
            <button
              key={i}
              data-testid={`suggested-question-${i}`}
              onClick={() => ask(q)}
              className="text-sm px-3 py-1.5 rounded-full border border-[#DCE3DF] text-[#5A6B63] hover:border-[#0E6E52] hover:text-[#0E6E52] transition-colors duration-200"
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {result && (
        <div className="space-y-6" data-testid="insights-result">
          <div className="bg-[#E4F1EB] border border-[#0E6E52]/20 rounded-[10px] p-6 flex gap-3">
            <Sparkles className="w-5 h-5 text-[#0E6E52] shrink-0 mt-0.5" />
            <p className="text-base text-[#141E1A] leading-relaxed" data-testid="insight-answer">
              {result.answer}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {result.kpis.map((kpi, i) => (
              <KpiCard key={i} kpi={kpi} />
            ))}
          </div>

          <InsightChart chart={result.chart} />

          <div className="bg-white border border-[#DCE3DF] rounded-[10px] shadow-[0_4px_20px_rgba(20,30,26,0.06)] p-6">
            <SqlPanel sql={result.sql} />
          </div>
        </div>
      )}
    </main>
  );
}
